import { Component, inject, effect, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Popover } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { UserService } from '@core/services/user.service';
import { SidebarMenuService } from '@core/services/sidebar-menu.service';
import { ProveedorService } from '@core/services/proveedor.service';
import { ToastMessageService } from '@core/services/toast-message.service';
import { APP_ROUTES } from '@core/constants/app-routes.const';
import { UserAccount } from '@core/models/userAccount.model';
import { getUserInitials } from '@core/utils/user-initials.util';
import { GlobalSearchComponent } from '@shared/components/global-search/global-search.component';
import { UserMenuComponent } from '@shared/components/user-menu/user-menu.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    FormsModule,
    SelectModule,
    Popover,
    TooltipModule,
    TranslocoModule,
    GlobalSearchComponent,
    UserMenuComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly sidebarMenuService = inject(SidebarMenuService);
  private readonly proveedorService = inject(ProveedorService);
  private readonly toast = inject(ToastMessageService);
  private readonly transloco = inject(TranslocoService);

  isSidebarOpen: boolean = false;
  userName: string | null = null;

  readonly companies = signal<UserAccount[]>([]);
  companyInfo: any = null;
  selectedCompanyId: number | null = null;
  selectedProveedorExternoId: number | null = null;

  readonly isCompaniesLoading = signal(false);
  readonly isInfoLoading = signal(false);

  private lastCompaniesLoadedForUserName: string | null = null;
  private lastInfoLoadedKey: string | null = null;

  constructor() {
    effect(() => {
      this.userName = this.userService.userName();
    });

    // Cargar lista de empresas por usuario (MasterPage legacy dropdown).
    effect(() => {
      const userName = this.userService.userName();
      if (!userName) return;
      if (this.lastCompaniesLoadedForUserName === userName) return;

      this.lastCompaniesLoadedForUserName = userName;
      this.isCompaniesLoading.set(true);
      this.companies.set([]);
      this.companyInfo = null;

      this.proveedorService.getEmpresas(userName).subscribe({
        next: (res) => {
          const companies = res.data ?? [];
          this.companies.set(companies);
          this.isCompaniesLoading.set(false);

          const currentCompanyId = this.userService.companyId();
          const currentProveedorId = this.userService.proveedorId();

          if (!currentCompanyId && (res.data?.length ?? 0) > 0) {
            const firstCompany = companies[0];
            if (firstCompany?.proveedorExternoID != null) {
              this.userService.setProveedorId(firstCompany.proveedorExternoID);
              this.selectedProveedorExternoId = firstCompany.proveedorExternoID;
            }
            this.userService.setCompanyId(firstCompany.empresaID, true);
          } else {
            this.selectedCompanyId = currentCompanyId;
            // Sincronizar el dropdown con el proveedorExternoID activo en localStorage
            const activeCompany = companies.find(
              (c) => c.proveedorExternoID === currentProveedorId,
            );
            const fallbackCompany = companies.find(
              (c) => c.empresaID === currentCompanyId,
            );
            this.selectedProveedorExternoId =
              activeCompany?.proveedorExternoID ??
              fallbackCompany?.proveedorExternoID ??
              null;

            // Si proveedorId no estaba en localStorage, guardarlo ahora para que el dashboard pueda cargar.
            if (
              currentProveedorId == null &&
              this.selectedProveedorExternoId != null
            ) {
              this.userService.setProveedorId(this.selectedProveedorExternoId);
            }
          }
        },
        error: () => {
          this.isCompaniesLoading.set(false);
          this.toast.showError(
            this.transloco.translate('common.error'),
            this.transloco.translate('NAVBAR.ERR_COMPANIES'),
          );
        },
      });
    });

    // Cargar info-empresa cuando cambie userName o companyId.
    effect(() => {
      const userName = this.userService.userName();
      const companyId = this.userService.companyId();
      this.selectedCompanyId = companyId;

      if (!userName || companyId === null) return;

      const key = `${userName}|${companyId}`;
      if (this.lastInfoLoadedKey === key) return;
      this.lastInfoLoadedKey = key;

      this.isInfoLoading.set(true);
      this.proveedorService
        .getInfoEmpresa({ userName, empresaId: companyId })
        .subscribe({
          next: (res) => {
            const d = res.data;
            this.companyInfo = d;
            const pid = d?.proveedorID ?? d?.proveedorId ?? d?.ProveedorID;
            if (pid != null && Number.isFinite(Number(pid))) {
              this.userService.setProveedorId(Number(pid));
            }
            this.isInfoLoading.set(false);
          },
          error: () => {
            this.isInfoLoading.set(false);
            this.toast.showError(
              this.transloco.translate('common.error'),
              this.transloco.translate('NAVBAR.ERR_COMPANY_INFO'),
            );
          },
        });
    });

    effect(() => {
      this.isSidebarOpen = this.sidebarMenuService.isSidebarOpen();
    });
  }

  toggleSidebar(): void {
    const next = !this.isSidebarOpen;
    this.sidebarMenuService.setSidebarOpen(next);
    this.sidebarMenuService.setIsNavBtnSelected(next);
  }

  goToHome(): void {
    this.router.navigate([APP_ROUTES.DASHBOARD]);
  }

  getFullName(): string {
    return (
      this.userService.getUserData()?.fullName ??
      this.userName ??
      this.transloco.translate('NAVBAR.USER')
    );
  }

  getInitials(): string {
    return getUserInitials(this.getFullName());
  }

  getCompanyName(): string {
    return (
      this.companies().find(
        (c) => c.proveedorExternoID === this.selectedProveedorExternoId,
      )?.razonSocial ?? ''
    );
  }

  onCompanyChange(nextProveedorExternoId: number): void {
    if (
      !nextProveedorExternoId ||
      nextProveedorExternoId === this.userService.proveedorId()
    )
      return;
    const selectedCompany = this.companies().find(
      (c) => c.proveedorExternoID === nextProveedorExternoId,
    );
    if (!selectedCompany) return;
    this.userService.setProveedorId(nextProveedorExternoId);
    this.userService.setCompanyId(selectedCompany.empresaID, true);
  }
}
